// ============================================================================
// Attribution — DERIVED, NEVER ASKED. 2026-09-02.
//
// Two facts, both already stored, neither ever on the form:
//   WHO TYPED IT  → projects.created_by_eid
//   WHOSE IT IS   → the account manager who held that building AT THE TIME
//
// Jack Stuber types most Kansas City project work on behalf of the account
// manager. That is normal and correct. One field cannot carry both facts, and
// carrying only the first makes the screen say Jack owns 157 records he does
// not. So the row is two clauses: "Entered by Jack · Melanie's account".
//
// ⛔ Do NOT add a form field for either. The person is the signed-in user and
// the owner is the account manager on the building. Asking would invite a wrong
// answer where a right one is already on file.
//
// ⛔ TCSD-0150 — a script-loaded row shows NO PERSON. `source` is how this
// database records that a load, not a human, wrote the row; naming a living
// employee there is attributing 345 imported records to whoever happened to run
// the script. The load is named in plain words instead, or nothing.
//
// The walk itself is v_project_attribution. This file only turns its answer into
// the words on the screen, so both are testable and neither is inline.
// ============================================================================

/** One row of v_project_attribution. EIDs only — names come from the resolver. */
export interface AttributionRow {
  project_id: string;
  entered_by_eid: string | null;
  entered_by_source: string | null;
  owner_eid: string | null;
  /**
   * How much the owner name is worth.
   *   recorded         the audit trail covers this date
   *   unchanged_since  the building has not moved, but the date predates the
   *                    trail — the earliest value on file, not a record
   *   landon_fallback  Ben's ruling for undeterminable pre-October-2025 records
   *   unresolved       no building, or the building holds no manager
   */
  owner_basis: 'recorded' | 'unchanged_since' | 'landon_fallback' | 'unresolved';
}

/** EID → the name to print. Backed by v_employee_display, every status. */
export type NameLookup = (eid: string | null) => string | null;

/**
 * The load, in words a person recognises. `source` has exactly one value in the
 * table today (`Trello_Backfill`), but the shape is general so a second import
 * does not print a column value at the user.
 */
export function sourceLabel(source: string): string {
  const cleaned = source.replace(/_?backfill$/i, '').replace(/[_-]+/g, ' ').trim();
  return cleaned ? `Imported from ${cleaned}` : 'Imported';
}

/** "Melanie" → "Melanie's". "Travis" → "Travis'". */
function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

/** First name only — the row is a byline, not a directory entry. */
function shortName(full: string): string {
  return full.trim().split(/\s+/)[0] ?? full;
}

/**
 * WHO TYPED IT, or the load that did, or an honest admission. Never blank, never
 * "Unknown" — that reads as a name we failed to find rather than a record that
 * predates the field.
 */
export function enteredClause(row: AttributionRow, nameFor: NameLookup): string {
  if (row.entered_by_source) return sourceLabel(row.entered_by_source);
  const name = row.entered_by_eid ? nameFor(row.entered_by_eid) : null;
  if (name) return `Entered by ${shortName(name)}`;
  return 'Entered before this was tracked';
}

/**
 * WHOSE IT IS. Null when there is genuinely no one to name — 13 projects carry
 * no building at all, and a building with no manager cannot produce an owner.
 * The caller renders nothing rather than a placeholder.
 */
export function ownerClause(row: AttributionRow, nameFor: NameLookup): string | null {
  if (!row.owner_eid) return null;
  const name = row.owner_eid ? nameFor(row.owner_eid) : null;
  if (!name) return null;
  return `${possessive(shortName(name))} account`;
}

/**
 * ⚠️ Whether the owner name is a record or the earliest value on file. The
 * account-manager trail begins 2026-04-29 and 64% of projects are dated before
 * it, so without this the screen presents an inference exactly like a fact.
 * Returned as a tooltip, not as visible chrome — it is a caveat, not a warning.
 */
export function ownerCaveat(row: AttributionRow): string | null {
  switch (row.owner_basis) {
    case 'unchanged_since':
      return 'This building has not changed hands since we began recording changes on 29 Apr 2026. Its manager on this date is not separately recorded.';
    case 'landon_fallback':
      return 'No manager could be determined for this date and the record predates October 2025, so it resolves to Landon Kohen.';
    default:
      return null;
  }
}

/** Both clauses, in reading order. Used on the detail screen. */
export function attributionLine(row: AttributionRow, nameFor: NameLookup): string {
  const owner = ownerClause(row, nameFor);
  const entered = enteredClause(row, nameFor);
  return owner ? `${entered} · ${owner}` : entered;
}
